import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined"
import { Link, Typography } from "@mui/material"
import { ReactElement, useState } from "react"

import {
    DB_ASSESSOR_DEMAND,
    DB_ASSESSOR_GEN,
    DNO_APPROVED,
    ENA_SYS_ADMIN,
    INSTALLER,
    INSTALLER_ADMIN,
    LCT_MANU_APPROVED,
    PUBLIC,
} from "./roles"
import FaqAssessApplication from "../assets/images/faq_assess_application.png"
import FaqPlugInSolar from "../assets/images/faq_plug-in_solar.png"
import MfaHelpSteps from "../components/account/mfa/mfaHelpSteps"
import AppButton from "../components/buttons/appButton"
import ImageTips from "../components/popUps/imageTips/imageTips"
import { getSupportPortalUrl } from "../hooks/useSupportPortalUrl"
import * as routes from "../router/routes"

export interface IQuestion {
    category: string
    question: string
    answer: ReactElement | string
    videoId?: string
    permittedRoles: string[]
}

const FONT_WEIGHT = 300

const ALL_ROLES = [
    INSTALLER,
    DNO_APPROVED,
    LCT_MANU_APPROVED,
    DB_ASSESSOR_DEMAND,
    DB_ASSESSOR_GEN,
    ENA_SYS_ADMIN,
]

const ALL_ROLES_WITH_PUBLIC = [...ALL_ROLES, PUBLIC]

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
// eslint-disable-next-line no-undef
const CONFIG = GLOBAL_CONFIG

const { supportPortalUrl } = getSupportPortalUrl()

const GETTING_SET_UP = "Getting Set Up"
const GENERAL = "General"
const APPLICATION = "Connection Applications"
const DATABASE_EXTRACTS = "Database Extracts"
const PLUG_IN_SOLAR_DEVICE_GUIDANCE = "Plug-in Solar Device Guidance"
const COMMENTS = "Comments"
const COMMISSIONING = "Commissioning"
export const DEVICES = "For Manufacturers"
const FOR_DNOS = "For DNOs"
const MFA = "Logging in with MFA (Multi Factor Authentication)"

export const getFaqCategoryDisplayName = (
    category: string,
    groups: string[]
): string => {
    if (category === DEVICES) {
        const isAssessor = groups.some((role) =>
            [DB_ASSESSOR_DEMAND, DB_ASSESSOR_GEN].includes(role)
        )
        if (isAssessor) {
            return "For Assessors"
        }
        if (groups.includes(LCT_MANU_APPROVED)) {
            return "For Manufacturers"
        }
    }

    return category
}

export const categoryToAnchorIdMap: Record<string, string> = {
    [GETTING_SET_UP]: "getting-set-up",
    [GENERAL]: "general",
    [APPLICATION]: "application",
    [DATABASE_EXTRACTS]: "db-extracts",
    [PLUG_IN_SOLAR_DEVICE_GUIDANCE]: "plug-in-solar-device-guidance",
    [COMMENTS]: "comments",
    [COMMISSIONING]: "commissioning",
    [DEVICES]: "devices",
    [FOR_DNOS]: "for-dnos",
    [MFA]: "mfa",
}

const FaqImageTips = (): ReactElement => {
    const [showImageTips, setShowImageTips] = useState(false)
    return (
        <>
            <AppButton
                sx={{ mt: 0, marginTop: 2 }}
                onClick={() => setShowImageTips(true)}
                variant="outlined"
                color="primary"
                size="small"
                endIcon={<LightbulbOutlinedIcon />}
            >
                Tips
            </AppButton>
            <ImageTips
                isOpen={showImageTips}
                close={() => setShowImageTips(false)}
            />
        </>
    )
}

export const QUESTIONS: IQuestion[] = [
    {
        category: GETTING_SET_UP,
        question:
            "My company has registered with Connect Direct - how do I sign up?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Once your company has been registered by the company
                administrator, you can create your individual account.
                <br />
                <ol>
                    <li>
                        Create an account using your{" "}
                        <b>company email address</b>
                    </li>
                    <li>
                        Your account will automatically link to your company
                    </li>
                    <li>
                        Your Company Administrator must approve your account
                        before you can access the platform
                    </li>
                </ol>
                If your company does not have company email addresses, you will
                need to request an invitation from your company administrator.
                <br />
                <br />
            </Typography>
        ),
        permittedRoles: [
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
            DNO_APPROVED,
            ENA_SYS_ADMIN,
            INSTALLER,
            INSTALLER_ADMIN,
            LCT_MANU_APPROVED,
            PUBLIC,
        ],
        videoId: "7MW7ccEYTYI",
    },
    {
        category: GETTING_SET_UP,
        question: "I'm an installer - how do I get started?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Whether you&apos;re an independent installer or part of a larger
                organisation, you can register your company on ENA Connect
                Direct to submit and manage domestic LCT applications in one
                place.
                <br />
                <br />
                Getting started is simple:
                <ol>
                    <li>
                        Create an account and sign in:{" "}
                        <Link href={supportPortalUrl} target="_blank">
                            Connect Direct Support Portal
                        </Link>
                    </li>
                    <li>Select “Register Company” during setup </li>
                    <li>
                        Follow the steps to complete your company registration
                    </li>
                </ol>
                If you register the company, you will automatically become the
                Company Administrator. You can:
                <ul>
                    <li>Invite team members </li>
                    <li>Review and approve new users </li>
                    <li>Oversee all applications submitted by your team</li>
                </ul>
                Team members who sign up using the company email domain will be
                linked to your company and must be approved before gaining
                access.
            </Typography>
        ),
        permittedRoles: [ENA_SYS_ADMIN, INSTALLER, INSTALLER_ADMIN, PUBLIC],
        videoId: "yxFc5zm1WaM",
    },
    {
        category: GENERAL,
        question: "What is Connect Direct?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Connect Direct is a national platform developed by the UK’s
                Distribution Network Operators (DNOs) in partnership with the
                Energy Networks Association (ENA).
                <br />
                <br />
                It enables installers to submit and manage domestic low-carbon
                technology applications, including solar PV, batteries, EV
                charge points and heat pumps, through a single platform. ENA
                <br />
                <br />
                It is used to obtain approval before fitting low-carbon
                technology (LCT) devices, both generation and demand, to
                existing residential properties. The platform standardises and
                streamlines the application process across all DNOs in Great
                Britain and Northern Ireland.
                <br />
                <br />
                Manufacturers also use Connect Direct to register their approved
                devices such as inverters, EV chargers, and heat pumps, making
                them available for installers to select when submitting
                applications.
                <br />
                <br />
                ENA Connect Direct:
                <ul>
                    <li>
                        Standardises data flow and communication for installers
                    </li>
                    <li>Enhances data feedback for DNOs</li>
                    <li>
                        Improves speed, consistency, and accuracy across
                        applications
                    </li>
                </ul>
                By working with installers who use ENA Connect Direct, customers
                can be confident that installations are safe and meet all
                required obligations.
                <br />
                <br />
                This platform represents a significant step forward for the UK
                energy system, supporting the transition from a centralised,
                one-way power network to a bidirectional, smart, and flexible
                grid, providing DNOs with improved visibility of network design
                and operation.
            </Typography>
        ),
        permittedRoles: [
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
            DNO_APPROVED,
            ENA_SYS_ADMIN,
            INSTALLER,
            INSTALLER_ADMIN,
            LCT_MANU_APPROVED,
            PUBLIC,
        ],
    },
    {
        category: MFA,
        question: "How do I generate an MFA Code?",
        answer: <MfaHelpSteps />,
        permittedRoles: [
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
            DNO_APPROVED,
            ENA_SYS_ADMIN,
            INSTALLER,
            INSTALLER_ADMIN,
            LCT_MANU_APPROVED,
            PUBLIC,
        ],
    },
    {
        category: MFA,
        question: "How do I reset my MFA?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                An administrator within your organisation can reset your MFA
                using their{" "}
                <Link href={routes.ADMIN_PORTAL} target="_blank">
                    User Management Portal
                </Link>{" "}
                - share this link with them.
                <br />
                <br />
                If you are unable to contact your organisation’s admin, you can
                raise a ticket through the{" "}
                <Link href={supportPortalUrl} target="_blank">
                    Connect Direct Support Portal
                </Link>{" "}
                for assistance.
            </Typography>
        ),
        permittedRoles: ALL_ROLES_WITH_PUBLIC,
    },

    {
        category: GETTING_SET_UP,
        question:
            "I’m a software provider or API Installer - how do I get started?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Software providers and API Installers can use our enterprise
                APIs to programmatically submit applications to DNOs and receive
                application updates.
                <br />
                <br />
                We provide:
                <ul>
                    <li>OpenAPI-standard documentation</li>
                    <li>A sandbox environment for testing</li>
                    <li>Production API access</li>
                    <li>Access to a support portal</li>
                </ul>
                Please follow the steps below to get started:
                <br />
                <ol>
                    <li>Create an account and sign-in</li>
                    <li>Pick ‘Software Provider’ during the set-up process</li>
                    <li>
                        We will give you access to our API documentation there
                        and then
                    </li>
                    <li>
                        We will provide access to our support portal where you
                        can raise a ticket requesting API keys for our
                        production and sandbox environments
                    </li>
                    <li>We will create your keys and issue them to you</li>
                </ol>
            </Typography>
        ),
        permittedRoles: [INSTALLER, ENA_SYS_ADMIN, PUBLIC],
    },
    {
        category: GETTING_SET_UP,
        question: "I'm a manufacturer - how do I get started?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                If you&apos;re a manufacturer, you can register your company on
                Connect Direct to manage your products and make them available
                for installers to select when submitting DNO applications.
                <br />
                <br />
                Getting started is simple:
                <ol>
                    <li>Create an account and sign in to Connect Direct. </li>
                    <li>
                        Select &quot;Manufacturer&quot; during the account setup
                        process/ (on the set-up screen).
                    </li>
                    <li>
                        Set up your company by following the guided steps.
                        You&apos;ll become the company administrator.
                    </li>
                    <li>
                        Once your company is set up, team members can register
                        using their company email address. They&apos;ll
                        automatically be linked to your organisation, and as the
                        administrator, you&apos;ll need to approve their access
                        before they can use the platform.
                    </li>
                </ol>
                <u>Existing ENA device registrations</u>
                <br />
                <br />
                If your devices were previously registered on the ENA Device
                Registers, an additional verification step is required to
                protect your existing data. All devices registered with ENA
                before the launch of Connect Direct in May 2024 have already
                been migrated to the Connect Direct platform. Once your company
                has been verified, you&apos;ll be able to manage these devices
                within Connect Direct.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED, PUBLIC],
    },
    {
        category: GENERAL,
        question: "How do I reset my MFA?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                If you need to reset your Multi-Factor Authentication (MFA),
                this must be completed by an admin within your organisation.
                <br />
                <br />
                Organisation Admins and System Admins can reset MFA through the
                User Management Portal in the Manage Users section.
                <br />
                <br />
                To reset MFA:
                <br />
                <ol>
                    <li>Locate the user in the Manage Users interface</li>
                    <li>Select the Reset MFA button next to the user’s name</li>
                </ol>
                Once the reset is completed:
                <ul>
                    <li>
                        The user will receive an automated confirmation email
                    </li>
                    <li>
                        The email will contain a secure link to the ENA Connect
                        Direct portal
                    </li>
                    <li>
                        The user will be prompted to reconfigure their MFA
                        settings
                    </li>
                </ul>
                Organisation Admins and System Admins can also reset MFA for
                themselves.
            </Typography>
        ),
        permittedRoles: [
            DNO_APPROVED,
            ENA_SYS_ADMIN,
            INSTALLER,
            INSTALLER_ADMIN,
            LCT_MANU_APPROVED,
        ],
    },
    {
        category: GENERAL,
        question: "How do I get help?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Our support team is available to assist with any queries or
                issues.
                <br />
                To contact support:
                <ol>
                    <li> Log in to Connect Direct. </li>
                    <li>
                        Raise a ticket via the{" "}
                        <Link href={CONFIG.SN_PORTAL_URL} target="_blank">
                            Connect Direct Support Portal
                        </Link>
                    </li>
                </ol>
                For guidance on how to raise a ticket, please refer to the video
                guide:
            </Typography>
        ),
        permittedRoles: [
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
            DNO_APPROVED,
            ENA_SYS_ADMIN,
            INSTALLER,
            INSTALLER_ADMIN,
            LCT_MANU_APPROVED,
            PUBLIC,
        ],
        videoId: "-jEtx4CQT40",
    },
    {
        category: GENERAL,
        question: "How do I use the search panel?",
        answer: "The search panel is a great way to get where you need to go quickly. Click the search bar or press (MAC) ⌘+K or (WINDOWS) CTRL+K to launch",
        permittedRoles: [ENA_SYS_ADMIN],
    },

    {
        category: GENERAL,
        question: "How do I raise a support ticket",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                You can raise a ticket here via our{" "}
                <Link href={CONFIG.SN_PORTAL_URL} target="_blank">
                    Connect Direct Support Portal
                </Link>
                <br />
                <br />
                For more information on how to raise a ticket, please refer to
                our video guide below
            </Typography>
        ),
        permittedRoles: [ENA_SYS_ADMIN],
        videoId: "-jEtx4CQT40",
    },
    {
        category: GENERAL,
        question: "I cannot see an installer company in the company search",
        answer: "Please note, only installers who opt to set up as a company on Connect Direct are listed here. Those that set up as solo installers do not appear on the company search (the ability to register as a solo installer was removed Dec 2024)",
        permittedRoles: [ENA_SYS_ADMIN],
    },
    {
        category: GENERAL,
        question: "Company details are missing from the company search",
        answer: "If you believe that company details are missing from the company search, please contact our support team via the Connect Direct Support Portal",
        permittedRoles: [ENA_SYS_ADMIN],
    },

    {
        category: GENERAL,
        question: "How do I change my company contact details?",
        answer: "To update your company contact details, please raise a support ticket via the Support Portal and our team will assist you",
        permittedRoles: [LCT_MANU_APPROVED, INSTALLER_ADMIN],
    },

    {
        category: FOR_DNOS,
        question:
            "How can I update DNO thresholds and configurations on the platform?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                To update your thresholds, your Company Admin must assign the
                Threshold Manager role to two members of your team:
                <br />
                <br />- One user to create and submit threshold changes
                <br />- A second user to review and approve or reject those
                changes
                <br />
                <br />
                For governance purposes, it is recommended that one of the
                Threshold Managers is your nominated Head of Connections
                representative. For any changes to email templates, raise a
                support ticket requesting the change. Written sign-off from your
                nominated Head of Connections representative is required before
                changes can be implemented.
                <br />
                <br />
                Once the ticket has been created, the support team will request
                the necessary approval. To help speed up the process, we
                recommend obtaining sign-off before submitting the ticket. After
                approval has been received, the support team will implement the
                requested changes and notify you once completed.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: FOR_DNOS,
        question: "How do I search for a company?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The Company Search feature allows you to find any installer or
                manufacturer that has registered their company with Connect
                Direct.
                <br />
                <br />
                You can use this feature to:
                <ul>
                    <li> Search for registered installer companies. </li>
                    <li>Search for registered manufacturer companies. </li>
                    <li>
                        View company details and verify whether an organisation
                        is registered with Connect Direct.
                    </li>
                </ul>
                For step-by-step guidance, watch the video below:
                <br />
            </Typography>
        ),
        videoId: "XMoymFymoYI",
        permittedRoles: [DNO_APPROVED, ENA_SYS_ADMIN],
    },
    {
        category: FOR_DNOS,
        question: "Can I assign applications to members of my team?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Yes. Connect Direct allows you to assign applications to members
                of your team, making it easier to manage workloads and track
                ownership.
                <br />
                <br />
                With application assignment, you can:
                <ul>
                    <li>Assign applications to specific team members.</li>
                    <li>
                        Manage workflows by allocating applications to the
                        appropriate user.
                    </li>
                    <li>
                        Update the assignment status using a generic status to
                        indicate that an application has been processed or
                        resolved within your own internal systems.
                    </li>
                </ul>
                This feature helps teams coordinate work more effectively while
                maintaining visibility of application ownership and progress.
                <br />
                <br />
                For guidance on how to assign an application, please refer to
                the video guide:
            </Typography>
        ),
        videoId: "srPdFUyhyF8",
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: APPLICATION,
        question: "Who can submit an LCT application?",
        answer: "Anyone who has been instructed to install an Electric Vehicle charger, Heat Pump, or domestic generation asset can submit an application. This includes installers acting on behalf of a customer. However, it remains the responsibility of the connecting customer to ensure that the correct data is submitted.",
        permittedRoles: [
            INSTALLER,
            LCT_MANU_APPROVED,
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
            ENA_SYS_ADMIN,
            PUBLIC,
        ],
    },
    {
        category: APPLICATION,
        question: "Who can submit an LCT application?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Anyone competent to install an electric vehicle charger, heat
                pump, or domestic generation asset can submit an application.
                This includes installers acting on behalf of the customer.
                <br />
                <br />
                However, it remains the responsibility of the connecting
                customer to ensure that the correct data is submitted.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: APPLICATION,
        question: "When do I need to make an application?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                An application must be submitted every time an installer or
                customer wishes to install a Solar PV system, Battery, Electric
                Vehicle Charge Point or Heat Pump on an existing property.
                <br />
                <br />
                All Connect Direct applications must be made pre-installation.
                Connect Direct cannot be used for speculative applications
            </Typography>
        ),
        permittedRoles: [ENA_SYS_ADMIN, INSTALLER, INSTALLER_ADMIN, PUBLIC],
    },
    {
        category: APPLICATION,
        question: "How do I submit an application?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Submitting applications through Connect Direct is
                straightforward. Please follow the steps outlined in the video
                below.
                <br />
                <br />
                We have also introduced an MPAN lookup feature, meaning you no
                longer need to enter an MPAN when completing an application. We
                will use the address details provided to determine the MPAN for
                you.
                <br />
                <br />
                If an MPAN cannot be found, you will be prompted to enter it
                manually once the application has been assessed.
            </Typography>
        ),
        permittedRoles: [ENA_SYS_ADMIN, INSTALLER, INSTALLER_ADMIN],
        videoId: "FMK9ThUmtgI",
    },
    {
        category: APPLICATION,
        question: "What is a Duplicate Application",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                If another application has recently been submitted for the same
                address, you may see a duplicate warning before submission,
                regardless of the technology type or the organisation submitting
                it.
                <br />
                <br />
                When you click Submit, you will either:
                <ul>
                    <li>Be taken directly to the assessment page, or</li>
                    <li>
                        See a pop-up warning listing recent applications at the
                        same address.
                    </li>
                </ul>
                The information displayed will vary depending on your user role.
                <br />
                <br />
                Installer Admin
                <ul>
                    <li>Your applications</li>
                    <li>
                        Applications from installers within your organisation
                    </li>
                    <li>Applications raised outside your organisation</li>
                </ul>
                Installer
                <ul>
                    <li>Your applications</li>
                    <li>Applications raised outside your organisation</li>
                </ul>
                For applications outside your organisation, only the technology
                type and creation date are visible.
                <br />
                <br />
                To proceed, click Confirm or to cancel, click Cancel to return
                to the Review and Submit page.
            </Typography>
        ),
        permittedRoles: [INSTALLER],
        videoId: "",
    },
    {
        category: APPLICATION,
        question: "What is a Duplicate Application",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                If another application has recently been submitted for the same
                address, the installer may see a duplicate warning before
                submission, regardless of the technology type or the
                organisation submitting it.
                <br />
                <br />
                After clicking Submit, they will either:
                <ul>
                    <li>Be taken directly to the assessment page, or</li>
                    <li>
                        See a pop-up warning listing recent applications at the
                        same address.
                    </li>
                </ul>
                What you can see depends on your role.
                <br />
                <br />
                For applications outside their organisation, only the technology
                type and creation date are visible.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
        videoId: "",
    },
    {
        category: APPLICATION,
        question: "How do I view my applications?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Select the{" "}
                <Link href={routes.INSTALLER_LIST_APPLICATIONS}>
                    My Applications
                </Link>{" "}
                tab to view all submitted applications.
            </Typography>
        ),
        permittedRoles: [ENA_SYS_ADMIN, INSTALLER, INSTALLER_ADMIN],
        videoId: "E8LeQ1lnyVE",
    },
    {
        category: APPLICATION,
        question: "Can I amend an application after submission?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Yes - most applications can be amended after submission.
                <br />
                <br />
                However, applications with the following statuses cannot be
                amended:
                <ul>
                    <li>Escalated / Re-Escalated to DNO</li>
                    <li>
                        Awaiting Assessment / Image Scanning Under Assessment
                    </li>
                    <li>
                        Commissioning Approved / Update Required / Submitted
                    </li>
                    <li>Export MPAN Submitted</li>
                </ul>
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "How do I assess an amended application?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The application amendments are reassessed under the same
                application ID and may be:
                <ul>
                    <li>(Re) Auto-approved</li>
                    <li>(Re) Escalated to DNO</li>
                </ul>
                If (Re) Escalated, they will appear in your queue and assessed
                like new applications. Applications cannot be amended if they
                are with the following statuses:
                <ul>
                    <li>Escalated / Re-escalated to DNO</li>
                    <li>Awaiting assessment or image scanning</li>
                    <li>
                        Commissioning approved / update required / submitted
                    </li>
                    <li>Export MPAN submitted</li>
                </ul>
                To view what has changed in the application, you can use one of
                the following methods:
                <ol type="a">
                    <li>
                        View changes in the Application Summary:
                        <br />
                        <br />
                        Navigate to the relevant component on the Application
                        Summary page. Any amended details will be displayed
                        within the expandable/collapsible sections, as shown in
                        the example below.
                        <br />
                        <br />
                        <img
                            src={FaqAssessApplication}
                            alt="Application Summary"
                            style={{
                                maxWidth: "90%",
                                height: "auto",
                            }}
                        />
                    </li>
                    <br />
                    <br />
                    <li>
                        View changes via the API:
                        <br />
                        <br />
                        If you are an API user, you can review amended fields
                        within the amendmentHistory array.
                        <br />
                        <br />
                        For support with API integrations or interpreting
                        amendment data, please contact our support team via the{" "}
                        <Link href={supportPortalUrl} target="_blank">
                            Connect Direct Support Portal
                        </Link>
                    </li>
                </ol>
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: APPLICATION,
        question: "How do I handle multiple LCTs at a property?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Installers should submit a single application including all
                LCTs. They should not create multiple applications.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: APPLICATION,
        question: "How do I create a draft application?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Creating a draft is simple:
                <ul>
                    <li>
                        A draft is automatically created after completing the
                        first page and clicking Next
                    </li>
                    <li>
                        Drafts auto-save every 15 seconds and whenever you click
                        Next
                    </li>
                    <li>A Draft ID will be visible at the top of the page</li>
                    <li>
                        All drafts are available in the My Drafts section, where
                        you can sort and filter them
                    </li>
                    <li>Drafts are automatically deleted after 6 months.</li>
                    <li>
                        One month before deletion, the draft will be flagged.
                    </li>
                    <li>Drafts can be manually deleted using the bin icon.</li>
                    <li>
                        To continue a draft, click on it to reopen and edit the
                        application. Once submitted, the draft is removed.
                    </li>
                </ul>
            </Typography>
        ),
        permittedRoles: [INSTALLER],
        videoId: "iZLibR0fnqU",
    },
    {
        category: APPLICATION,
        question: "What if I’m installing multiple LCTs at one property?",
        answer: "Submit a single application containing all low-carbon technologies being installed at the property.",
        permittedRoles: [ENA_SYS_ADMIN, INSTALLER, INSTALLER_ADMIN, PUBLIC],
    },
    {
        category: APPLICATION,
        question: "What networks are supported by Connect Direct?",
        answer: "Connect Direct supports all Distribution Network Operators (DNOs) across Great Britain and Northern Ireland, as well as several Independent Distribution Network Operators (IDNOs).",
        permittedRoles: [
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
            ENA_SYS_ADMIN,
            INSTALLER,
            INSTALLER_ADMIN,
            LCT_MANU_APPROVED,
        ],
    },
    {
        category: APPLICATION,
        question: "How do I calculate a property's Max Demand?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Maximum Demand helps determine whether the property’s
                electricity supply can support new equipment on the network.
                Maximum Demand considers that not all loads in a property will
                operate simultaneously (circuit, property, section of network or
                network).
                <br />
                <br />
                Guidance on assessing supply adequacy is available in the IET
                Code of Practice for EV Charging Equipment Installation.
                <br />
                <br />
                Approaches to determining Maximum Demand:
                <ol>
                    <li>
                        Use Existing Information - where load data for the
                        property is available.
                    </li>
                    <li>
                        Determine Installed Loads - apply diversity factors
                        using recognised industry guidance, such as:
                        <ul>
                            <li>
                                Institution of Engineering and Technology Wiring
                                Regulations (BS 7671, 18th Edition)
                            </li>
                            <li>Electrical Installation Design Guide</li>
                            <li>Calculations for Electricians and Designers</li>
                            <li>On-Site Guide (BS 7671:2018)</li>
                        </ul>
                    </li>
                </ol>
                Some installers use remote monitoring devices that clamp a split
                Current Transformer (CT) around the supply meter tail and
                collect load data is collected over approximately two weeks, at
                ten-minute intervals, to determine accurate Maximum Demand.{" "}
                <br />
                <br />
                For further guidance, contact the Institution of Engineering and
                Technology (IET).
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "What is a cut-out?",
        answer: (
            <>
                <Typography fontWeight={FONT_WEIGHT}>
                    A cut-out is the piece of equipment that connects the
                    network operator’s electricity supply cable to the
                    property’s internal wiring.
                    <br />
                    <br />
                    To identify the type of cut-out installed at a property and
                    its rating, you should first inspect the equipment safely
                    and, if necessary, contact your local Distribution Network
                    Operator (DNO) for confirmation.
                    <br />
                    <br />
                    Certain older cut-outs may require upgrading by the network
                    operator.
                    <br />
                    <br />
                    It is mandatory to upload a photo of the cut-out as part of
                    the Connect Direct application process. Click on the tips
                    button for guidance.
                </Typography>
                <FaqImageTips />
            </>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "How do I identify issues with the property's cut-out?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Guidance on identifying issues with cut-outs and other network
                equipment is available from the National Association of
                Professional Inspectors and Testers (NAPIT).
                <br />
                <br />
                If you are unsure whether a cut-out is suitable for a proposed
                installation, reviewing photographs prior to a site visit can
                help identify potential issues at an early stage.
                <br />
                <br />
                Installers sometimes request photographs before a site visit to
                identify potential issues early.
                <br />
                <br />
                You should also refer to relevant ENA guidance including:
                <ul>
                    <li>
                        <Link
                            href="https://napit.org.uk/pdf/guidance-on-reporting-issues-with-distribution-businesses-service-equipment.pdf"
                            target="_blank"
                        >
                            Guidance on reporting issues with Distribution
                            Business’ service equipment
                        </Link>
                    </li>
                    <li>
                        <Link
                            href="https://www.napit.org.uk/pdf/supplementary-guidance-distribution-businesses-emergency-codes-explained.pdf"
                            target="_blank"
                        >
                            Supplementary Guidance on Distribution Business
                            Emergency Codes
                        </Link>
                    </li>
                </ul>
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "What is an MPAN and where can I find it?",
        answer: "The MPAN (Meter Point Administration Number) is the unique identifier for an electricity supply point and can be found on the property’s electricity bill.",
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "What is an import or load limiting device?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                An import or load limiting device is hardware and/or software
                that restricts the amount of current drawn by a premises.
                <br />
                <br />
                For EV chargers or Heat Pumps, this may operate as a curtailment
                scheme.
                <br />
                <br />
                When completing an application:
                <ul>
                    <li>
                        Maximum Demand calculations must factor in the load
                        limiting device.
                    </li>
                    <li>
                        The installer is responsible for ensuring values
                        submitted are accurate
                    </li>
                    <li>
                        The load limiting device must operate fail-safe —
                        meaning that in the event of failure, connection limits
                        are not exceeded.
                    </li>
                </ul>
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "What happens when my application is escalated?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                If an application cannot be auto-approved, it is escalated to
                the relevant DNO.
                <br />
                <br />
                The DNO’s connections team will review the application and issue
                a decision.
                <br />
                <br />
                You will be notified of the outcome via Connect Direct.
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "How do I handle hybrid inverters?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Hybrid inverters are listed in the Live Device Register on
                Connect Direct.
                <br />
                <br />
                When submitting an application, first try to find the device in
                the register and once located note the category of the device.
                <br />
                <br />
                If listed as:
                <ul>
                    <li>
                        <strong>PV</strong> → Submit as a Solar PV application
                    </li>
                    <li>
                        <strong>Energy Storage System</strong> → Submit as a
                        Battery application
                    </li>
                </ul>
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "What is Advanced Search?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The Advanced Search function allows installers to refine device
                searches when creating a new application or managing an existing
                one. It allows you to apply filters to narrow down your search
                results.
                <br />
                <br />
                It is available within the New Devices and Existing Devices
                sections of the application form.
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "How does Connect Direct assess applications?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Connect Direct uses a data-driven decision engine to assess
                applications.
                <br />
                <br />
                It checks things like:
                <ul>
                    <li>The safety of the cut-out and supply equipment</li>
                    <li>Whether the supply capacity is sufficient</li>
                    <li>Whether the property is on a looped supply</li>
                    <li>Whether a network upgrade is required</li>
                    <li>
                        The Maximum Demand of the property is within DNO
                        thresholds
                    </li>
                    <li>Equipment compliance</li>
                    <li>
                        Whether the equipment within DNO-defined approval
                        thresholds
                    </li>
                </ul>
                If all checks pass, the application is auto approved in real
                time. If not, it is escalated to the DNO for review. Any updates
                will be communicated to you via Connect Direct.
                <br />
                <br />
                Each DNO configures its own auto-approval thresholds, which may
                vary geographically.
            </Typography>
        ),
        permittedRoles: [
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
            ENA_SYS_ADMIN,
            INSTALLER,
            INSTALLER_ADMIN,
            LCT_MANU_APPROVED,
            PUBLIC,
        ],
    },
    {
        category: APPLICATION,
        question: "How are applications assessed?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Connect Direct uses a data-driven decision engine to assess
                applications.
                <br />
                <br />
                It checks things like:
                <ul>
                    <li>The safety of the cut-out and supply equipment</li>
                    <li>Whether the supply capacity is sufficient</li>
                    <li>Whether the property is on a looped supply</li>
                    <li>Whether a network upgrade is required</li>
                    <li>
                        The Maximum Demand of the property is within DNO
                        thresholds
                    </li>
                    <li>Equipment compliance</li>
                    <li>
                        Whether the equipment within DNO-defined approval
                        thresholds
                    </li>
                </ul>
                If all checks pass, the application is auto approved in real
                time. If not, it is escalated to the DNO for review. Any updates
                will be communicated to you via Connect Direct.
                <br />
                <br />
                Each DNO configures its own auto-approval thresholds, which may
                vary geographically.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: APPLICATION,
        question: "How do I view applications requiring my attention as a DNO?",
        answer: "To view all applications, including those that have been escalated for review, please follow the steps demonstrated in the video below.",
        permittedRoles: [DNO_APPROVED],
        videoId: "Tch1Hj6LWSA",
    },
    {
        category: APPLICATION,
        question: "Why do some applications include looped supply checks?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                {" "}
                For generation applications below a defined installed capacity
                threshold, a looped supply check is not required. In these
                cases, the looped supply assessment criteria will not be
                displayed during the application assessment process.
                <br />
                <br />
                This threshold can be configured within the platform settings.
                If you would like to update the threshold value, please raise a
                ticket with our support team.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: APPLICATION,
        question: "What is the Cut-Out Rating Threshold?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The cut-out rating (Amps) entered by installers during the
                connection application process is now automatically overridden
                by the rating configured for each cut-out type:
                <ul>
                    <li>Grey Plastic </li>
                    <li>Black Plastic / Phenolic Resin </li>
                    <li>Metal Clad </li>
                </ul>
                This helps ensure that a cut-out rating is always available,
                reducing the likelihood of applications being escalated due to
                missing or incorrect installer-entered values. If no rating has
                been provided, the system will apply a default value based on
                the selected cut-out type.
                <br />
                <br />
                Both installer-entered and DNO-assumed ratings are visible in
                the application summary page.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: APPLICATION,
        question: "What happens to my applications after 6 months?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                After six months, applications are partially encrypted to remove
                personally identifiable information (PII), such as Customer name
                and contact details.
                <br />
                <br />
                You can still access them in{" "}
                <Link href={routes.INSTALLER_LIST_APPLICATIONS}>
                    My Applications
                </Link>{" "}
                by selecting <b>Redacted applications over 6 months</b> in the
                filter drawer.
            </Typography>
        ),
        permittedRoles: [INSTALLER_ADMIN],
    },
    {
        category: APPLICATION,
        question: "What happens to applications after 6 months?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                After six months, applications are partially encrypted to remove
                personally identifiable information (PII), such as Customer name
                and contact details.
                <br />
                <br />
                You can still access them in{" "}
                <Link href={routes.INSTALLER_LIST_APPLICATIONS}>
                    My Applications
                </Link>{" "}
                by selecting Redacted applications over 6 months in the filter
                drawer.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question: "Who is responsible for registering the product?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The manufacturer or assembler is responsible for registering the
                product and submitting the required information and evidence for
                assessment.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question: "How do I submit a product for assessment?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The registration process is largely the same as registering any
                other LCT device, with two key differences:
                <br />
                <br />
                <strong>1. Complete the Form C attachment</strong>
                <br />
                <br />
                You must complete and submit the Form C attachment as part of
                your device registration. Make sure you select the &apos;Plug-in
                solar device&apos; option on the front cover of the form - which
                you can find{" "}
                <Link
                    href="https://www.ena-eng.org/ena-docs/Index?Action=ViewDetail&EID=102993&PEID=103005"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    here
                </Link>
                .
                <br />
                <br />
                <strong>2. Register the complete plug-in solar unit</strong>
                <br />
                <br />
                Registrations must be for the complete plug-in solar unit. We do
                not accept registrations for inverter-only products under the
                plug-in solar category. To register a complete plug-in solar
                unit:
                <img
                    src={FaqPlugInSolar}
                    alt="Plug-in Solar"
                    style={{
                        display: "block",
                        margin: "32px auto",
                        width: "70%",
                        height: "auto",
                    }}
                />
                <ol>
                    <li>Select the &apos;Generation Device&apos; tile</li>
                    <li>
                        Select &apos;Plug-In Solar&apos; from the device type
                        dropdown
                    </li>
                    <li>
                        Complete and submit the registration, including the
                        required Form C attachment.
                    </li>
                </ol>
                If you only want to register an inverter, select the
                &apos;PV&apos; device type instead.
                <br />
                <br /> Watch this video to review the LCT device registration
                process:
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "xnLziz6tuzE",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "Why does the ENA Connect Direct register require the complete plug-in solar product to be registered?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The Type Test Register (TTR) provides network operators with a
                centralised database for assessing network compliance.
                <br />
                <br />
                For plug-in solar products, manufacturers must register the
                complete plug-in solar unit. This allows network operators and
                customers to clearly identify what product is being installed
                and verify that the complete unit is compliant.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question: "What evidence is required?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Manufacturers must provide sufficient technical information and
                evidence to demonstrate compliance with ENA EREC G98 Issue 2
                Amendment 1.
                <br />
                <br />
                This includes:
                <ul>
                    <li>
                        Details and test reports for the inverter used in the
                        unit
                    </li>
                    <li>
                        A completed G98 Form C, with the Plug-in Solar checkbox
                        selected
                    </li>
                    <li>Specifications for the solar panels</li>
                    <li>Specifications for the cables</li>
                    <li>Specifications for the plug</li>
                </ul>
                This information is required to demonstrate that the submission
                relates to a complete plug-in solar unit, rather than an
                inverter-only product.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "My product contains an inverter that is already on the register. Do I still need to submit the complete product?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Yes. The complete plug-in solar unit must still be registered
                and submitted for assessment. Where the inverter has already
                been assessed as compliant, you can include its System Reference
                Number in the Plug-in Solar section of the G98 Form C.
                <br />
                <br />
                Manufacturers are encouraged to use inverter products that have
                been assessed against G98 within the previous 18 months.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "What if I sell the same inverter with different panel configurations?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                You do not need to submit every panel configuration separately.
                <br />
                <br />
                As long as each configuration complies with EREC G98 Issue 2
                Amendment 1 and uses the same component parts, you do not need
                to submit each arrangement for a separate assessment.
                <br />
                <br />
                For example, using different types of mounting brackets does not
                require a new assessment.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "Do I need to submit technical evidence for components that have already been approved?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Where a component, such as an inverter, has already been
                assessed as compliant, you can reference the existing assessment
                rather than submitting the same evidence again.
                <br />
                <br />
                See &apos;My product contains an inverter that is already on the
                register. Do I still need to submit the complete product?&apos;
                for further information.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "Why does the register show product names instead of inverter model numbers?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The model of the complete plug-in solar unit is displayed in the
                fifth column of the Live Device Register.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "Does an ENA Connect Direct entry approve every product sold under the same family or brand name?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Not automatically.
                <br />
                <br />
                ENA can assess compliance at a family or brand level where there
                is sufficient technical and operational similarity between the
                products.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question: "What do ‘Compliant’ and ‘Non-compliant’ mean?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Compliant means the manufacturer has provided sufficient
                evidence to demonstrate that the unit meets the requirements of
                EREC G98 Issue 2 Amendment 1.
                <br />
                <br />
                Non-compliant means that compliance has not yet been
                sufficiently demonstrated. This may be because information is
                missing, incomplete or requires clarification before the
                assessment can continue.
                <br />
                <br />
                DNOs are unlikely to accept units that have not been assessed as
                Compliant for connection to the network.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "My product is shown as ‘Non-compliant’ but we are still providing information. What should I do?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Check the Comments section of your submission. The assessor will
                provide guidance on any information that needs to be corrected,
                added or clarified.
                <br />
                <br />
                Once you have addressed the requested changes, update your
                submission and resubmit it for assessment.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question: "Why does my product not appear on the register?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                It is the manufacturer or assembler&apos;s responsibility to
                register the product and submit it for assessment.
                <br />
                <br />
                If your product does not appear on the register, first check
                that the registration and submission process has been completed.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "When does a replacement or repair require a new product configuration or technical assessment?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Any modification to the product or unit requires an amendment to
                the existing registration.
                <br />
                <br />
                Depending on the nature of the modification, additional
                technical information or assessment may be required.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "Which changes are options within an approved product, and which create a separate product variant?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                See &apos;When does a replacement or repair require a new
                product configuration or technical assessment?&apos; for further
                information.
                <br />
                <br />
                This depends on whether the change affects the technical or
                operational characteristics of the approved unit.
                <br />
                <br />
                If a modification changes any components of the product or the
                unit configuration, an amendment to the registration is
                required.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "How should hardware, firmware and software revisions be managed?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Any modification to the product or unit, including relevant
                hardware, firmware or software changes, should be managed
                through an amendment to the existing registration.
                <br />
                <br />
                The amendment process allows the changes to be assessed against
                the existing product entry.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question:
            "Can the same electrical kit be sold with ground, wall, fence and balcony mounts under one ENA reference?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Yes. ENA encourages manufacturers to submit a base model for
                assessment. This should include the core electrical components,
                such as:
                <ul>
                    <li>Inverter</li>
                    <li>Solar panels</li>
                    <li>Cables</li>
                    <li>Plug</li>
                </ul>
                Different mounting options, such as ground, wall, fence or
                balcony mounts, can then be offered without requiring a separate
                submission for each mounting arrangement, provided the
                underlying approved unit remains unchanged.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: PLUG_IN_SOLAR_DEVICE_GUIDANCE,
        question: "Can a compliant kit be sold second-hand?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Yes. Once a unit has been assessed as compliant, it can be moved
                or sold for reconnection to the network, provided it continues
                to meet the requirements of EREC G98.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "",
    },
    {
        category: DEVICES,
        question:
            "I'm a manufacturer who has previously registered devices, how do I access them?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                If you previously registered devices on the Type Test Register,
                ENA Heat Pump Register, or ENA EV Charge Point Register, your
                devices have already been migrated to Connect Direct.
                <br />
                <br />
                To access and manage your existing devices:
                <ol>
                    <li>
                        Register your company on Connect Direct (see
                        &quot;I&apos;m a manufacturer. How do I get
                        started?&quot; above).
                    </li>
                    <li>Complete the company verification process.</li>
                    <li>
                        Once verified, you&apos;ll be able to view and
                        administer your migrated devices within Connect Direct.
                    </li>
                </ol>
                Note: An additional verification step is required to ensure only
                authorised representatives can access your companies existing
                device data.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
    },
    {
        category: DEVICES,
        question: "Does Connect Direct replace the Type Test Register?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Yes. Connect Direct has replaced the Type Test Register.
                <br />
                <br />
                All devices previously registered on the Type Test Register have
                been migrated to Connect Direct, and the legacy Type Test
                Register is no longer accepting new device registrations.
                <br />
                <br />
                Manufacturers should now use Connect Direct to register, manage,
                and publish their devices.
            </Typography>
        ),
        permittedRoles: [
            LCT_MANU_APPROVED,
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
            PUBLIC,
        ],
    },
    {
        category: DEVICES,
        question: "How do I publish a device as a manufacturer?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Once your company has been registered and verified, you can
                publish devices directly through Connect Direct.
                <br />
                <br />
                For a step-by-step walkthrough, watch the guidance video below.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED, PUBLIC],
        videoId: "xnLziz6tuzE",
    },
    {
        category: DEVICES,
        question: "How does a manufacturer publish a device?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Once their company has been registered and verified,
                manufacturers can publish devices directly through Connect
                Direct.
                <br />
                <br />
                For a step-by-step walkthrough, watch the guidance video below.
            </Typography>
        ),
        permittedRoles: [DB_ASSESSOR_DEMAND, DB_ASSESSOR_GEN],
        videoId: "xnLziz6tuzE",
    },
    {
        category: DEVICES,
        question: "What is the difference between a Version and an Amendment?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Versions and amendments are used for different types of changes
                to a device:
                <br />
                <ul>
                    <li>
                        <b>Amendments</b> should be used to correct errors or
                        omissions in an existing device submission where the
                        device itself has not changed.{" "}
                    </li>
                    <li>
                        <b>Versions</b> should be created when there has been a
                        change to the device’s <b>hardware or software</b>,
                        resulting in a new version of the product.
                    </li>
                </ul>
                In short:
                <ul>
                    <li>
                        <b>Amendment</b> = administrative or compliance
                        correction
                    </li>
                    <li>
                        <b>Version</b> = product change
                    </li>
                </ul>
            </Typography>
        ),
        permittedRoles: [
            LCT_MANU_APPROVED,
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
        ],
    },
    {
        category: DEVICES,
        question: "What happens when an alert is raised?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                An alert is used to notify stakeholders of an operational issue
                affecting a device on the electricity network.
                <br />
                <br />
                When an alert is raised:
                <ul>
                    <li>
                        An automatic notification is sent to all relevant DNOs,
                        ENA system administrators, and the device manufacturer
                    </li>
                    <li>
                        The alert remains associated with the device until it
                        has been reviewed and resolved
                    </li>
                    <li>
                        Only Manufacturer Administrators and ENA System
                        Administrators can remove an alert once the issue has
                        been addressed
                    </li>
                </ul>
            </Typography>
        ),
        permittedRoles: [
            LCT_MANU_APPROVED,
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
        ],
    },
    {
        category: DEVICES,
        question: "How are devices assessed?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Manufacturers can submit new devices for inclusion in the Type
                Test Register or Demand Asset Registers through Connect Direct.
                <br />
                <br />
                To submit a device:
                <ol>
                    <li>Sign in to Connect Direct.</li>
                    <li>Select Publish New Device.</li>
                    <li>
                        Complete the device registration form with all required
                        information.
                    </li>
                    <li>Submit the application for assessment.</li>
                </ol>
                Their submission will be reviewed by an ENA Assessor, who will
                verify the information provided and determine the device&apos;s
                compliance status.
                <br />
                <br />
                <b>Please allow up to six weeks </b> for the assessment process
                to be completed.
            </Typography>
        ),
        permittedRoles: [DB_ASSESSOR_DEMAND, DB_ASSESSOR_GEN],
    },
    {
        category: DEVICES,
        question: "How are devices assessed?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Manufacturers can submit new devices for inclusion in the Type
                Test Register or Demand Asset Registers through Connect Direct.
                <br />
                <br />
                To submit a device:
                <ol>
                    <li>Sign in to Connect Direct.</li>
                    <li>Select Publish New Device.</li>
                    <li>
                        Complete the device registration form with all required
                        information.
                    </li>
                    <li>Submit the application for assessment.</li>
                </ol>
                Your submission will be reviewed by an ENA Assessor, who will
                verify the information provided and determine the device&apos;s
                compliance status.
                <br />
                <br />
                <b>Please allow up to six weeks </b> for the assessment process
                to be completed.
            </Typography>
        ),
        permittedRoles: [LCT_MANU_APPROVED],
    },
    {
        category: DEVICES,
        question: "What are alerts?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Alerts provide a way for Manufacturers and DNOs to notify other
                stakeholders about issues identified with a device operating on
                the network.
                <br />
                <br />
                When an alert is raised:
                <ul>
                    <li>
                        All DNOs and the device manufacturer are notified
                        automatically.
                    </li>
                    <li>
                        The alert helps ensure operational issues are visible
                        across the industry.
                    </li>
                    <li>
                        Once the issue has been resolved, the manufacturer can
                        submit supporting evidence to the ENA System
                        Administrators.
                    </li>
                    <li>
                        The alert will only be removed after the ENA is
                        satisfied that the appropriate corrective actions have
                        been taken.
                    </li>
                </ul>
            </Typography>
        ),
        permittedRoles: [
            LCT_MANU_APPROVED,
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
        ],
    },
    {
        category: COMMISSIONING,
        question: "What is commissioning, and why is it needed?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Commissioning confirms that installed low-carbon technology
                (LCT) have been correctly installed, tested and is operating
                safely, according to the design specifications and requirements.
                <br />
                <br />
                As the deployment of low-carbon technologies increases, network
                operators must be aware of the number and location of energy
                assets within their network to maintain the safe and efficient
                operation of the electricity network.
                <br />
                <br />
                Once your connection application is approved, you can install
                the system. After installation, you must complete performance
                testing and submit commissioning details through the
                commissioning process on ENA Connect Direct.
                <br />
                <br />
                Commissioning is required if the customer wants to export
                electricity and receive payments under the Smart Export
                Guarantee (SEG).
            </Typography>
        ),
        permittedRoles: [INSTALLER],
        videoId: "JlZIK5kpYVg",
    },
    {
        category: COMMISSIONING,
        question: "What is commissioning, and why is it needed?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Commissioning confirms that installed low-carbon technology
                (LCT) has been correctly installed, tested and is operating
                safely, according to the design specifications and requirements.
                As the deployment of low-carbon technologies increases, network
                operators must be aware of the number and location of energy
                assets within their network to maintain the safe and efficient
                operation of the electricity network.
                <br />
                <br />
                Once approval has been received for the installation of a
                low-carbon generation technology; such as Solar PV, Battery
                Storage, or Vehicle-to-Grid (V2G) equipment - the installer can
                proceed with the installation.
                <br />
                <br />
                After installation is complete, the installer must submit
                commissioning information and performance test results to the
                DNO through the ENA Connect Direct commissioning process.
                <br />
                <br />
                Commissioning is required if the customer wants to export
                electricity and receive payments under the Smart Export
                Guarantee (SEG).
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
        videoId: "qJf8ZtKL8ak",
    },
    {
        category: COMMISSIONING,
        question: "What technologies require commissioning information?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Commissioning information is required for generating
                technologies such as:
                <ul>
                    <li>Solar PV</li>
                    <li>Battery storage</li>
                    <li>Vehicle-to-Grid (V2G)</li>
                </ul>
                Heat pumps and standard EV charge points do not require
                commissioning information.
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question: "Can I submit commissioning information in Connect Direct?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Yes. Once your application is approved, you can complete the
                commissioning form via the{" "}
                <Link href={routes.INSTALLER_LIST_APPLICATIONS}>
                    My Applications
                </Link>{" "}
                tab.
                <br />
                <br />
                If you are integrated via API, commissioning can also be
                submitted that way.
            </Typography>
        ),
        permittedRoles: [ENA_SYS_ADMIN, INSTALLER, INSTALLER_ADMIN, PUBLIC],
    },
    {
        category: COMMISSIONING,
        question: "Do DNOs review my commissioning information? ",
        answer: "Yes. Commissioning details are sent to the relevant Distribution Network Operator (DNO), who will either approve them or request further information.",
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question:
            "How can I see commissioning information provided by the installer",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                You can view the applications with commissioning information on
                your &apos;Awaiting review&apos; tab.
                <br />
                <br />
                If the application has been auto-approved, you will be able to
                view any device information from the Device&apos;s details page.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: COMMISSIONING,
        question:
            "How do I respond to the commissioning information provided by the installer",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Approved applications containing generation devices will display
                a &apos;Submit&apos; button within the commissioning column.
                <br />
                <br />
                Selecting this button will open the Commissioning Web Form,
                where commissioning details and test results can be entered and
                submitted.
                <br />
                <br />A comissioning application will be automatically approved
                if the total installed capacity is below the thresholds set by
                the DNO. Automatically approved applications will be marked with
                a &apos;Comissioning Accepted&apos;
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: COMMISSIONING,
        question: "What if the installation changes?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                If the installed system differs from the approved application,
                you must submit an amendment to the application before
                completing commissioning.
                <br />
                <br />
                The amended application will be reassessed and either (Re)
                auto-approved or (Re) escalated. Once approved, you can submit
                commissioning as normal.
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question: "What if the installation changes?",

        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                If the final installation differs from the originally approved
                application, the installer must first submit an amendment before
                commissioning information can be provided.
                <br />
                <br />
                The amended application will be reassessed under the same
                application ID and will either be:
                <ul>
                    <li>(Re) Auto-approved</li>
                    <li>(Re) Escalated to DNO</li>
                </ul>
                If the application is re-escalated, it will appear in your
                assessment queue and should be reviewed in the same way as a new
                application.
                <br />
                <br />
                Once the amended application has been approved, commissioning
                information can then be submitted.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: COMMISSIONING,
        question: "Can commissioning be submitted via API?",
        answer: "Yes. A dedicated commissioning API is available. Forms are version-controlled to maintain compatibility.",
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question: "When is commissioning required?",
        answer: "Commissioning is required once an approved application includes a generation component.",
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question: "What is an Export MPAN?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                An Export MPAN is a 13-digit reference number for every
                electricity connection point in the country.
                <br />
                <br />
                It is required to receive payments under the Smart Export
                Guarantee (SEG) to small-scale low-carbon generators.
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question: "Can I get an Export MPAN without commissioning?",
        answer: "No. Commissioning information must be submitted before an Export MPAN can be issued.",
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMENTS,
        question: "How do I leave comments?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The comment thread on a connection application now enables both
                installer and DNO users to toggle comments as read or unread.
                When a comment is sent, the comment thread will appear as unread
                to the recipient. Any user with access to the application can
                mark the comment thread as read, and the system will record who
                marked it and when.
                <br />
                <br />
                On the{" "}
                <Link href={routes.INSTALLER_LIST_APPLICATIONS}>
                    My Applications
                </Link>{" "}
                page, you&apos;ll be notified of any unread comments needing
                your attention by a blue dot on the right-hand side.
                Additionally, a new filter has been added to quickly locate
                applications with unread comments, making it easier to identify
                those requiring a response or review.
            </Typography>
        ),
        permittedRoles: [INSTALLER],
        videoId: "",
    },
    {
        category: COMMENTS,
        question: "How do I leave comments?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Each application includes a comment thread and enables both
                installer and DNO users to exchange comments. When a comment is
                sent, the comment thread will appear as unread to the recipient.
                Any user with access to the application can mark the comment
                thread as read, and the system will record who marked it and
                when.
                <br />
                <br />
                On the{" "}
                <Link href={routes.DNO_APPLICATIONS_NOTIFICATIONS}>
                    DNO All Applications
                </Link>{" "}
                page, you&apos;ll be notified of any unread comments needing
                your attention by a blue dot on the right-hand side.
                Additionally, a new filter has been added to quickly locate
                applications with unread comments, making it easier to identify
                those requiring a response or review.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
        videoId: "",
    },
    {
        category: COMMENTS,
        question: "How do comment notifications work on device applications?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The comment feature on device applications helps manufacturers
                and assessors collaborate more effectively by highlighting new
                activity.
                <br />
                <br />
                <b>Here&apos;s how it works</b>
                <br />
                <ul>
                    <li>When a new comment is added, the recipient will:</li>
                    <ul>
                        <li>See the comment thread marked as Unread</li>
                        <li>
                            Receive an email notification advising them that a
                            new comment has been posted.
                        </li>
                    </ul>
                    <li>
                        Any user with access to the device application can mark
                        the comment thread as Read once it has been reviewed.
                    </li>
                    <li>
                        The system records who marked the thread as read and
                        when, providing a clear audit trail
                    </li>
                </ul>
                <b>Finding unread comments</b>
                <br />
                <br />
                To help you stay on top of outstanding actions:
                <ul>
                    <li>
                        A blue dot is displayed alongside device applications
                        that contain unread comments.
                    </li>
                    <li>
                        You can also use the Unread Comments filter on the
                        device list to quickly identify applications that
                        require your attention or response.
                    </li>
                </ul>
            </Typography>
        ),
        permittedRoles: [
            LCT_MANU_APPROVED,
            DB_ASSESSOR_DEMAND,
            DB_ASSESSOR_GEN,
        ],
        videoId: "",
    },
    {
        category: COMMENTS,
        question: "What is a Device Subscription",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Subscribing enables email notifications for new comments on a
                device.
                <br />
                <br />
                Steps:
                <ol>
                    <li> Navigate to the device</li>
                    <li>Click Subscribe</li>
                    <li>
                        View subscribed devices via the Live Device Register
                    </li>
                </ol>
                To stop notifications, click Unsubscribe.
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED, ENA_SYS_ADMIN],
        videoId: "",
    },
    {
        category: DATABASE_EXTRACTS,
        question: "Can I extract Live Device Register updates via API?",
        answer: "Yes. Delta update APIs are available. Please refer to the official API documentation for details.",
        permittedRoles: [DNO_APPROVED, INSTALLER],
    },
]
